import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Flex,
    Text,
    Button,
    useToast,
    VStack,
    Box,
    Heading,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import InputField from 'components/formComponents/inputField';
import AuthApi from 'api/auth';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import { useAuth } from 'auth-context/auth.context';
import PopModal from '../customerUsers/components/addModal';
import { AddIcon } from '@chakra-ui/icons';

export default function ShowroomCustomerAccess() {
    const [otpId, setOtpId] = useState('');
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const history = useHistory();
    const { user, setVerifiedCustomerId } = useAuth();
    const toast = useToast();

    useEffect(() => {
        if (user && user.user_type !== 'SHOWROOM') {
            history.push('/admin/default');
        }
    }, [user, history]);

    const handleSendOtp = async (values) => {
        // Bypass OTP if the same number was just verified in this session
        const lastVerifiedMobile = sessionStorage.getItem('lastVerifiedMobile');
        const lastVerifiedCustomerId = sessionStorage.getItem('lastVerifiedCustomerId');

        if (values.phoneNumber === lastVerifiedMobile && lastVerifiedCustomerId) {
            toast({
                title: 'Already Verified',
                description: 'This mobile number was recently verified. Redirecting...',
                status: 'info',
                duration: 2000,
                isClosable: true,
                position: 'top',
            });
            setVerifiedCustomerId(lastVerifiedCustomerId);
            return history.push(`/admin/customeruser/${lastVerifiedCustomerId}`);
        }

        try {
            setLoading(true);
            const response = await AuthApi.sendVerificationOtp({
                phone_number: values.phoneNumber,
            });
            if (response.data.status_code === 200) {
                setOtpId(response.data.data.otp_id);
                setStep(2);
                toast({
                    title: 'OTP Sent',
                    description: 'Please enter the OTP sent to the customer.',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                    position: 'top',
                });
            } else {
                toast({
                    title: 'Error',
                    description: response.data.message || 'Failed to send OTP',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                    position: 'top',
                });
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Something went wrong',
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (values) => {
        try {
            setLoading(true);
            const response = await AuthApi.verifyWithOTP({
                otp_id: otpId,
                value: values.otp,
            });
            if (response.data.status_code === 200) {
                const customerId = response.data.data.userdetails?.id;
                if (!customerId) {
                    throw new Error('Customer ID not found in response');
                }
                toast({
                    title: 'Verified',
                    description: 'Customer verified successfully. Redirecting...',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                    position: 'top',
                });

                // Store verification results for bypass logic
                const phoneNumber = sessionStorage.getItem('temp_search_mobile');
                if (phoneNumber) {
                    sessionStorage.setItem('lastVerifiedMobile', phoneNumber);
                    sessionStorage.setItem('lastVerifiedCustomerId', customerId);
                }

                setVerifiedCustomerId(customerId);
                // Avoid delay for redirection
                history.push(`/admin/customeruser/${customerId}`);
            } else {
                toast({
                    title: 'Verification Failed',
                    description: response.data.message || 'Invalid OTP',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                    position: 'top',
                });
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Something went wrong',
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoading(false);
        }
    };

    const phoneSchema = Yup.object().shape({
        phoneNumber: Yup.string()
            .required('Phone number is required')
            .matches(/^[0-9]{10}$/, 'Phone number must be exactly 10 digits'),
    });

    const otpSchema = Yup.object().shape({
        otp: Yup.string()
            .required('OTP is required')
            .matches(/^[0-9]{4}$/, 'OTP must be exactly 4 digits'),
    });

    const handleRegistrationSuccess = (newCustomer) => {
        if (newCustomer && newCustomer.id) {
            // Save verified state for subsequent fast access
            if (newCustomer.phoneNumber) {
                sessionStorage.setItem('lastVerifiedMobile', newCustomer.phoneNumber);
                sessionStorage.setItem('lastVerifiedCustomerId', newCustomer.id);
            }
            setVerifiedCustomerId(newCustomer.id);
            history.push(`/admin/customeruser/${newCustomer.id}`);
        }
    };

    return (
        <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
            <Flex direction='column' alignItems='center' justifyContent='center'>
                <Card p='20px' width={{ base: '100%', md: '600px' }} mb='20px'>
                    {step === 1 && (
                        <VStack spacing={4} align='stretch'>
                            <Heading size='md' mb='4'>Find Customer</Heading>
                            <Formik
                                initialValues={{ phoneNumber: '' }}
                                validationSchema={phoneSchema}
                                onSubmit={(values, actions) => {
                                    sessionStorage.setItem('temp_search_mobile', values.phoneNumber);
                                    handleSendOtp(values, actions);
                                }}>
                                {({ isSubmitting }) => (
                                    <Form>
                                        <InputField
                                            label='Mobile Number'
                                            name='phoneNumber'
                                            type='text'
                                            placeholder='Enter 10 digit mobile number'
                                        />
                                        <Button
                                            mt='20px'
                                            w='100%'
                                            colorScheme='brand'
                                            type='submit'
                                            isLoading={loading || isSubmitting}>
                                            Send OTP
                                        </Button>
                                    </Form>
                                )}
                            </Formik>

                            <Flex align='center' my='20px'>
                                <Box flex='1' height='1px' bg='gray.200' />
                                <Text mx='4' color='gray.400' fontSize='sm'>OR</Text>
                                <Box flex='1' height='1px' bg='gray.200' />
                            </Flex>

                            <PopModal
                                head='Register New Customer'
                                btnText='Register New Customer'
                                Icon={<AddIcon />}
                                variant='brand'
                                w='100%'
                                onSuccess={handleRegistrationSuccess}
                                statusOptions={[{ value: 'UNVERIFIED', label: 'Unverified' }]}
                            />
                        </VStack>
                    )}

                    {step === 2 && (
                        <VStack spacing={4} align='stretch'>
                            <Heading size='md' mb='4'>Verify OTP</Heading>
                            <Text fontSize='sm' color='gray.500'>
                                Enter the 4-digit OTP sent to the customer
                            </Text>
                            <Formik
                                initialValues={{ otp: '' }}
                                validationSchema={otpSchema}
                                onSubmit={handleVerifyOtp}>
                                {({ isSubmitting }) => (
                                    <Form>
                                        <InputField
                                            label='OTP'
                                            name='otp'
                                            type='text'
                                            placeholder='Enter 4 digit OTP'
                                        />
                                        <Button
                                            mt='20px'
                                            w='100%'
                                            colorScheme='brand'
                                            type='submit'
                                            isLoading={loading || isSubmitting}>
                                            Verify OTP
                                        </Button>
                                        <Button
                                            mt='10px'
                                            w='100%'
                                            variant='ghost'
                                            onClick={() => setStep(1)}>
                                            Back
                                        </Button>
                                    </Form>
                                )}
                            </Formik>
                        </VStack>
                    )}
                </Card>
            </Flex>
        </Box>
    );
}
